import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { CanModify, Translations } from './configuration';
import { Typography } from '@material-ui/core';
import {
  AppSelect,
  AppTextField,
  AppSwitch,
  AppGroupText,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { SchemeTypes } from '../../../../config';
import * as RegistrationDetailSelectors from '../../../../redux/reducers/registration_detail_reducer/selectors';
import { getState } from '../../../../redux/store';
import { ReducerKeys } from '../../../../redux/reducers';
import { FormFields } from '../../configuration';
import { AppCheckbox } from 'components/molecules/form_controls';

interface IProps {
  mode: 'VIEW' | 'EDIT';
  scheme_type: SchemeTypes;
  is_agent: boolean;
  isExcluded: boolean;
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
}

interface IElementsProps {
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  element: ReactElement;
  id: string;
  rowStyle?: any;
}

export default function IdentificationDetailsForm(props: IProps): ReactElement {
  // console.log('Render IdentificationDetailsForm');
  const mode = props.mode;
  const SCHEME_TYPE = props.scheme_type;
  const IS_AGENT = props.is_agent;
  const errors = props.errors;
  const control = props.control;
  const isExcluded = props.isExcluded;

  function handleFormElements(
    ossSheme: number,
    intermediary: boolean = false
  ): IElementsProps[] {
    switch (ossSheme) {
      case SchemeTypes.EU:
        return euSchemeElements();
      case SchemeTypes.NON_EU:
        return nonEuSchemeElements();
      default:
        if (intermediary) {
          return importSchemeIntermediaryElements();
        } else {
          return importSchemeElements();
        }
    }
  }

  function euSchemeElements(): IElementsProps[] {
    return [
      {
        element: renderRegistrationIdField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'registrationId',
      },
      {
        element: renderUsernameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'username',
      },
      {
        element: renderCompanyNameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'companyName',
      },
      {
        element: renderVatNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 8,
        id: 'vatNumber',
      },
      {
        element: renderVatGroup(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'vatGroup',
        rowStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '88px',
        },
      },
      {
        element: renderTradingName(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'trading_name',
      },
    ];
  }

  function nonEuSchemeElements(): IElementsProps[] {
    return [
      {
        element: renderRegistrationIdField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'registrationId',
      },
      {
        element: renderUsernameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'username',
      },
      {
        element: renderCompanyNameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'companyName',
      },
      {
        element: renderVatNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'vatNumber',
      },
      {
        element: renderNationalTaxRefNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'nationalTaxRefNumber',
      },
      {
        element: renderPlaceOfBusiness(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'placeOfBusiness',
      },
      {
        element: renderIssuingCountry(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'issuingCountry',
      },
      {
        element: renderTradingName(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'trading_name',
      },
    ];
  }

  function importSchemeElements(): IElementsProps[] {
    return [
      {
        element: renderRegistrationIdField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'registrationId',
      },
      {
        element: renderUsernameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'username',
      },
      {
        element: renderIntermediaryField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'intermediary',
      },
      {
        element: renderCompanyNameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'companyName',
      },
      {
        element: renderVatNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'vatNumber',
      },
      {
        element: renderNationalTaxRefNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'nationalTaxRefNumber',
      },
      {
        element: renderPlaceOfBusiness(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'placeOfBusiness',
      },
      {
        element: renderIssuingCountry(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'issuingCountry',
      },
      {
        element: renderTradingName(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'trading_name',
      },
    ];
  }

  function importSchemeIntermediaryElements(): IElementsProps[] {
    return [
      {
        element: renderRegistrationIdField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'registrationId',
      },
      {
        element: renderUsernameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 6,
        id: 'username',
      },
      {
        element: renderCompanyNameField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 8,
        id: 'companyName',
      },
      {
        element: renderPlaceOfBusiness(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'placeOfBusiness',
      },
      {
        element: renderVatNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'vatNumber',
      },
      {
        element: renderNationalTaxRefNumberField(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'nationalTaxRefNumber',
      },
      {
        element: renderVatGroup(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 4,
        id: 'vatGroup',
        rowStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '88px',
        },
      },
      // {
      //   element: renderPlaceOfBusiness(
      //     errors,
      //     control,
      //     mode,
      //     SCHEME_TYPE,
      //     IS_AGENT,
      //     isExcluded
      //   ),
      //   size: 6,
      //   id: 'placeOfBusiness',
      // },
      // {
      //   element: renderIssuingCountry(
      //     errors,
      //     control,
      //     mode,
      //     SCHEME_TYPE,
      //     IS_AGENT,
      //     isExcluded
      //   ),
      //   size: 6,
      //   id: 'issuingCountry',
      // },
      {
        element: renderTradingName(
          errors,
          control,
          mode,
          SCHEME_TYPE,
          IS_AGENT,
          isExcluded
        ),
        size: 12,
        id: 'trading_name',
      },
    ];
  }

  const classes = useStyles();
  const elements = handleFormElements(SCHEME_TYPE, IS_AGENT);

  return (
    <div>
      <Typography variant="h5">
        <TranslatedText textMap={Translations.identification_details_header} />
      </Typography>
      <div className={classes.form}>
        <GridLayout elements={elements} />
      </div>
    </div>
  );
}

const renderRegistrationIdField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_registrationId"
      label="Registration ID"
      error={'identificationDetails_registrationId' in errors}
      name="identificationDetails_registrationId"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.identificationDetails_registrationId &&
        errors.identificationDetails_registrationId.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_registrationId'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderUsernameField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_username"
      label="Username"
      error={'identificationDetails_username' in errors}
      name="identificationDetails_username"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.identificationDetails_username &&
        errors.identificationDetails_username.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_username'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderIntermediaryField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_intermediary"
      label="Intermediary"
      error={'identificationDetails_intermediary' in errors}
      name="identificationDetails_intermediary"
      control={control}
      rules={{}}
      helperText={<TranslatedText textMap={Translations.intermediary_help} />}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_intermediary'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderCompanyNameField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_companyName"
      label={<TranslatedText textMap={Translations.company_name_label} />}
      error={'identificationDetails_companyName' in errors}
      name="identificationDetails_companyName"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.identificationDetails_companyName &&
        errors.identificationDetails_companyName.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_companyName'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_vatNumber"
      label={<TranslatedText textMap={Translations.vat_number_label} />}
      error={'identificationDetails_vatNumber' in errors}
      name="identificationDetails_vatNumber"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.identificationDetails_vatNumber &&
        errors.identificationDetails_vatNumber.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_vatNumber'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderNationalTaxRefNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_nationalTaxRefNumber"
      label="National Tax Ref. Number"
      error={'identificationDetails_nationalTaxRefNumber' in errors}
      name="identificationDetails_nationalTaxRefNumber"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.identificationDetails_nationalTaxRefNumber &&
        errors.identificationDetails_nationalTaxRefNumber.message
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_nationalTaxRefNumber'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderVatGroup = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    // <AppSwitch
    //   required
    //   id="identificationDetails_vatGroup"
    //   label="VAT Group"
    //   name="identificationDetails_vatGroup"
    //   control={control}
    //   rules={{}}
    //   disabled={
    //     !CanModify[SCHEME_TYPE](IS_AGENT).includes(
    //       'identificationDetails_vatGroup'
    //     ) ||
    //     mode === 'VIEW' ||
    //     isExcluded
    //   }
    // />
    <AppCheckbox
      name="identificationDetails_vatGroup"
      error={'identificationDetails_vatGroup' in errors}
      label="VAT Group"
      required
      control={control}
      rules={{}}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_vatGroup'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

interface ICountriessData {
  country_id: string;
  code: string;
  description: string;
  numeric_code: number;
}

const renderPlaceOfBusiness = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  let countries: Array<ICountriessData> = [];
  if (SCHEME_TYPE === SchemeTypes.IOSS)
    countries = RegistrationDetailSelectors.useGetImportCountries();
  else countries = RegistrationDetailSelectors.useGetCountriesNonMemberState();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.place_of_business_label.English,
  });

  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: country.description,
      });

  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.place_of_business_label} />}
      id="identificationDetails_placeOfBusiness"
      name="identificationDetails_placeOfBusiness"
      required
      error={'identificationDetails_placeOfBusiness' in errors}
      control={control}
      helperText={
        errors.identificationDetails_placeOfBusiness ? (
          errors.identificationDetails_placeOfBusiness.message
        ) : (
          <TranslatedText textMap={Translations.place_of_business_help} />
        )
      }
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_placeOfBusiness'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderIssuingCountry = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  let countries: Array<ICountriessData> = [];
  if (SCHEME_TYPE === SchemeTypes.IOSS)
    countries = RegistrationDetailSelectors.useGetImportCountries();
  else countries = RegistrationDetailSelectors.useGetCountriesNonMemberState();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.issuing_country_labels.English,
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: country.description,
      });

  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.issuing_country_labels} />}
      id="identificationDetails_issuingCountry"
      name="identificationDetails_issuingCountry"
      required
      error={'identificationDetails_issuingCountry' in errors}
      control={control}
      helperText={
        errors.identificationDetails_issuingCountry ? (
          errors.identificationDetails_issuingCountry.message
        ) : (
          <TranslatedText textMap={Translations.issuing_country_help} />
        )
      }
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_issuingCountry'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};

const renderTradingName = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT',
  SCHEME_TYPE: SchemeTypes,
  IS_AGENT: boolean,
  isExcluded: boolean
) => {
  return (
    <AppGroupText
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="identificationDetails_trading_name"
      label={Translations.trading_name.English}
      error={'identificationDetails_trading_name' in errors}
      name="identificationDetails_trading_name"
      control={control}
      rules={{}}
      helperText={
        errors.identificationDetails_trading_name ? (
          errors.identificationDetails_trading_name.message
        ) : (
          <TranslatedText textMap={Translations.trading_name_help} />
        )
      }
      disabled={
        !CanModify[SCHEME_TYPE](IS_AGENT).includes(
          'identificationDetails_trading_name'
        ) ||
        mode === 'VIEW' ||
        isExcluded
      }
    />
  );
};
